<template>
  <div class="report-page">
    <div class="report-header">
      <h1><el-icon><WarningFilled /></el-icon> 安全隐患上报</h1>
      <p class="subtitle">发现隐患，立即拍照上报</p>
    </div>

    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      class="report-form"
      @submit.prevent="handleSubmit"
    >
      <el-form-item label="部门" prop="department">
        <el-select
          v-model="form.department"
          placeholder="请选择部门"
          style="width: 100%"
          :loading="orgLoading"
          @change="onDeptChange"
        >
          <el-option
            v-for="d in departments"
            :key="d.name"
            :label="d.name"
            :value="d.name"
          />
        </el-select>
        <div v-if="orgError" class="org-error">
          <el-icon><WarningFilled /></el-icon> 组织架构加载失败：{{ orgError }}，请刷新重试
        </div>
      </el-form-item>

      <el-form-item label="科室" prop="subdepartment">
        <el-select
          v-model="form.subdepartment"
          placeholder="请选择科室"
          style="width: 100%"
          :disabled="!form.department || orgLoading"
        >
          <el-option
            v-for="item in subdepartments"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="区域/生产线" prop="area">
        <el-input
          v-model="form.area"
          placeholder="例如：1号线、仓库A区、设备区"
          maxlength="50"
          show-word-limit
        />
      </el-form-item>

      <el-form-item label="隐患类型" prop="hazardType">
        <el-select v-model="form.hazardType" placeholder="请选择隐患类型" style="width: 100%" :render-label="renderHazardLabel">
          <el-option
            v-for="item in hazardTypes"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          >
            <span class="hazard-option">
              <el-icon :size="18" :style="{ color: item.color }"><component :is="item.icon" /></el-icon>
              <span>{{ item.label }}</span>
            </span>
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="隐患描述" prop="description">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="4"
          placeholder="请简要描述隐患内容，包括位置、危险程度等"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <el-form-item label="现场照片/视频" prop="images">
        <div class="media-tabs">
          <el-radio-group v-model="mediaMode" size="default">
            <el-radio-button label="image">
              <el-icon><CameraFilled /></el-icon> 照片 {{ imageFiles.length ? `(${imageFiles.length})` : '' }}
            </el-radio-button>
            <el-radio-button label="video">
              <el-icon><VideoPlay /></el-icon> 视频 {{ videoItems.length ? `(${videoItems.length})` : '' }}
            </el-radio-button>
          </el-radio-group>
        </div>

        <div v-if="mediaMode === 'image' && imageFiles.length < 9" class="media-add-full" @click="triggerImageUpload">
          <el-icon :size="18"><Plus /></el-icon>
          <span>添加照片</span>
        </div>
        <div v-if="mediaMode === 'video' && videoItems.length < 3" class="media-add-full" @click="triggerVideoUpload">
          <el-icon :size="18"><Plus /></el-icon>
          <span>添加视频</span>
        </div>

        <div v-if="mediaMode === 'image' && imagePreviews.length" class="media-grid">
          <div v-for="(url, idx) in imagePreviews" :key="idx" class="media-item">
            <img :src="url" alt="隐患照片" />
            <button class="media-remove" type="button" @click="removeImage(idx)">×</button>
            <span class="media-index">{{ idx + 1 }}</span>
          </div>
        </div>

        <div v-if="mediaMode === 'video' && videoItems.length" class="media-grid">
          <div v-for="(v, idx) in videoItems" :key="idx" class="media-item media-video">
            <video :src="v.url" muted playsinline></video>
            <button class="media-remove" type="button" @click="removeVideo(idx)">×</button>
            <span class="media-index">{{ idx + 1 }}</span>
          </div>
        </div>

        <div class="upload-hint">{{ mediaMode === 'image' ? '最多 9 张，单张不超过 20MB（JPG/PNG/GIF/WebP）' : '最多 3 段，单段不超过 100MB（MP4/MOV/WebM）' }}</div>
        <input ref="imageInputRef" type="file" accept="image/*" multiple capture="environment" style="display:none" @change="onImagesChange" />
        <input ref="videoInputRef" type="file" accept="video/*" multiple capture="environment" style="display:none" @change="onVideosChange" />
      </el-form-item>

      <el-divider />

      <el-form-item label="上报人（选填）" prop="reporter">
        <el-input v-model="form.reporter" placeholder="姓名或工号" maxlength="30" />
      </el-form-item>

      <el-form-item label="联系方式（选填）" prop="contact">
        <el-input
          v-model="form.contact"
          placeholder="手机号或内线号码"
          maxlength="20"
        />
      </el-form-item>

      <el-form-item>
        <el-button
          type="primary"
          :loading="submitting"
          :disabled="submitting"
          class="submit-btn"
          size="large"
          @click="handleSubmit"
        >
          {{ submitting ? '提交中...' : '提交上报' }}
        </el-button>
      </el-form-item>
    </el-form>

    <!-- 提交成功对话框 -->
    <el-dialog
      v-model="successDialogVisible"
      title="提交成功"
      width="90%"
      :close-on-click-modal="false"
      class="success-dialog"
    >
      <div class="success-content">
        <el-icon class="success-icon" :size="64" color="#67c23a"><CircleCheckFilled /></el-icon>
        <h2>隐患上报成功！</h2>
        <div class="hazard-id-card">
          <span class="label">隐患编号：</span>
          <span class="value">{{ submittedId }}</span>
        </div>
        <p class="success-tip">请记录隐患编号，后续可通过该编号查询处理进度。</p>
      </div>
      <template #footer>
        <el-button type="primary" @click="resetForm" size="large" style="width: 100%">
          继续上报
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, h, resolveComponent } from 'vue'
import { ElMessage } from 'element-plus'
import { submitHazard, fetchOrg } from '../api'

const formRef = ref(null)
const imageInputRef = ref(null)
const videoInputRef = ref(null)
const submitting = ref(false)
const successDialogVisible = ref(false)
const submittedId = ref('')

const imageFiles = ref([])      // File[]
const imagePreviews = ref([])   // dataURL[]
const videoItems = ref([])      // [{ file, url }]
const mediaMode = ref('image')  // 'image' | 'video'

const departments = ref([])
const orgLoading = ref(true)
const orgError = ref('')

const subdepartments = computed(() => {
  const dept = departments.value.find(d => d.name === form.department)
  return dept ? (dept.subdepartments || []) : []
})

onMounted(loadOrg)
async function loadOrg() {
  orgLoading.value = true
  orgError.value = ''
  try {
    const r = await fetchOrg()
    departments.value = r.departments || []
  } catch (e) {
    orgError.value = e.message
    ElMessage.error('组织架构加载失败：' + e.message)
  } finally {
    orgLoading.value = false
  }
}

function onDeptChange() {
  form.subdepartment = ''
}

const hazardTypes = [
  { value: '设备隐患', label: '设备隐患', icon: 'Tools', color: '#E6A23C' },
  { value: '电气隐患', label: '电气隐患', icon: 'Bolt', color: '#F5A623' },
  { value: '消防隐患', label: '消防隐患', icon: 'Fire', color: '#F56C6C' },
  { value: '化学品隐患', label: '化学品隐患', icon: 'Warning', color: '#9B59B6' },
  { value: '作业行为隐患', label: '作业行为隐患', icon: 'User', color: '#409EFF' },
  { value: '5S隐患', label: '5S隐患', icon: 'Brush', color: '#67C23A' },
  { value: '通道占用', label: '通道占用', icon: 'Guide', color: '#00BCD4' },
  { value: '其他', label: '其他', icon: 'MoreFilled', color: '#909399' }
]

function renderHazardLabel(option) {
  const item = hazardTypes.find(t => t.value === option.value)
  if (!item) return option.label
  return h('span', { style: 'display:inline-flex;align-items:center;gap:6px' }, [
    h(resolveComponent(item.icon), { style: { color: item.color }, size: 16 }),
    option.label
  ])
}

const form = reactive({
  department: '',
  subdepartment: '',
  area: '',
  hazardType: '',
  description: '',
  images: [],
  reporter: '',
  contact: ''
})

const rules = {
  department: [{ required: true, message: '请选择部门', trigger: 'change' }],
  subdepartment: [{ required: true, message: '请选择科室', trigger: 'change' }],
  area: [{ required: true, message: '请填写区域或生产线', trigger: 'blur' }],
  hazardType: [{ required: true, message: '请选择隐患类型', trigger: 'change' }],
  description: [{ required: true, message: '请填写隐患描述', trigger: 'blur' }],
  images: [{
    validator: (rule, value, callback) => {
      if (imageFiles.value.length === 0) callback(new Error('请至少上传一张照片'))
      else callback()
    },
    trigger: 'change'
  }]
}

function triggerImageUpload() {
  imageInputRef.value?.click()
}
function triggerVideoUpload() {
  videoInputRef.value?.click()
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

function onImagesChange(event) {
  const files = Array.from(event.target.files || [])
  for (const file of files) {
    if (imageFiles.value.length >= 9) { ElMessage.warning('最多上传 9 张照片'); break }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      ElMessage.error('仅支持 JPG/PNG/GIF/WebP 格式的图片'); continue
    }
    if (file.size > 20 * 1024 * 1024) {
      ElMessage.error('单张图片不能超过 20MB'); continue
    }
    imageFiles.value.push(file)
    const reader = new FileReader()
    reader.onload = (e) => { imagePreviews.value.push(e.target.result) }
    reader.readAsDataURL(file)
  }
  event.target.value = ''
}

function removeImage(idx) {
  imageFiles.value.splice(idx, 1)
  imagePreviews.value.splice(idx, 1)
}

function onVideosChange(event) {
  const files = Array.from(event.target.files || [])
  for (const file of files) {
    if (videoItems.value.length >= 3) { ElMessage.warning('最多上传 3 段视频'); break }
    if (file.size > 100 * 1024 * 1024) {
      ElMessage.error('单段视频不能超过 100MB'); continue
    }
    videoItems.value.push({ file, url: URL.createObjectURL(file) })
  }
  event.target.value = ''
}

function removeVideo(idx) {
  const item = videoItems.value[idx]
  if (item) URL.revokeObjectURL(item.url)
  videoItems.value.splice(idx, 1)
}

async function handleSubmit() {
  if (!formRef.value) return

  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) {
    ElMessage.warning('请填写完所有必填项')
    return
  }

  submitting.value = true
  try {
    const formData = new FormData()
    formData.append('department', form.department)
    formData.append('subdepartment', form.subdepartment)
    formData.append('area', form.area)
    formData.append('hazardType', form.hazardType)
    formData.append('description', form.description)
    formData.append('reporter', form.reporter || '')
    formData.append('contact', form.contact || '')
    imageFiles.value.forEach(f => formData.append('images', f))
    videoItems.value.forEach(v => formData.append('videos', v.file))

    const result = await submitHazard(formData)
    submittedId.value = result.id
    successDialogVisible.value = true
    ElMessage.success('上报成功！')
  } catch (error) {
    ElMessage.error(error.message || '提交失败，请重试')
  } finally {
    submitting.value = false
  }
}

function resetForm() {
  successDialogVisible.value = false
  submittedId.value = ''
  form.department = ''
  form.subdepartment = ''
  form.area = ''
  form.hazardType = ''
  form.description = ''
  form.reporter = ''
  form.contact = ''
  imageFiles.value = []
  imagePreviews.value = []
  videoItems.value.forEach(v => URL.revokeObjectURL(v.url))
  videoItems.value = []
  formRef.value?.resetFields()
  if (imageInputRef.value) imageInputRef.value.value = ''
  if (videoInputRef.value) videoInputRef.value.value = ''
}
</script>

<style scoped>
.report-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0;
}

.report-header {
  text-align: center;
  padding: 40px 20px 20px;
  color: #fff;
}

.report-header h1 {
  font-size: 24px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.subtitle {
  margin-top: 8px;
  font-size: 14px;
  opacity: 0.85;
}

.report-form {
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 24px 20px;
  margin-top: 16px;
}

.upload-hint {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 6px;
}

/* 多图/多视频网格 */
.media-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
}

.media-tabs :deep(.el-radio-button__inner) {
  padding: 8px 20px;
  font-size: 15px;
}

.media-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.media-item {
  position: relative;
  width: calc(33.333% - 7px);
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
}

.media-item img,
.media-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.media-video video {
  background: #000;
}

.media-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.media-remove:hover {
  background: rgba(245, 108, 108, 0.9);
}

.media-index {
  position: absolute;
  bottom: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 8px;
  z-index: 1;
}

.media-add-full {
  width: 100%;
  height: 48px;
  border: 2px dashed #dcdfe6;
  border-radius: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
  font-size: 15px;
  margin-bottom: 10px;
}

.media-add-full:hover {
  border-color: #409eff;
  color: #409eff;
  background: #f0f7ff;
}

.submit-btn {
  width: 100%;
  height: 48px;
  font-size: 18px;
  border-radius: 24px;
}

.org-error {
  font-size: 12px;
  color: #f56c6c;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hazard-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.success-content {
  text-align: center;
  padding: 16px 0;
}

.success-icon {
  margin-bottom: 16px;
}

.success-content h2 {
  font-size: 22px;
  color: #303133;
  margin-bottom: 20px;
}

.hazard-id-card {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.hazard-id-card .label {
  color: #909399;
  font-size: 14px;
}

.hazard-id-card .value {
  color: #409eff;
  font-size: 20px;
  font-weight: bold;
  letter-spacing: 2px;
}

.success-tip {
  color: #909399;
  font-size: 14px;
}
</style>